import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Copy, Plus, RefreshCw, AlertCircle } from "lucide-react";
import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { toast } from "sonner";
import { useAuth } from "@/_core/hooks/useAuth";

interface Invite {
  id: number;
  email: string;
  role: string;
  createdAt: string;
  expiresAt: string | null;
  usedAt: string | null;
}

export default function AdminInvites() {
  const { user, isAuthenticated } = useAuth();
  const [, setLocation] = useLocation();
  const [invites, setInvites] = useState<Invite[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"operator" | "admin" | "supervisor">("operator");
  const [refreshing, setRefreshing] = useState(false);

  // Redirect non-admin users
  useEffect(() => {
    if (isAuthenticated && user && user.role !== 'admin') {
      setLocation("/");
    }
  }, [isAuthenticated, user, setLocation]);

  // Fetch invites on mount
  const fetchInvites = async () => {
    setLoading(true);
    try {
      const response = await fetch("/api/admin/invites");
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to fetch invites");
      }

      setInvites(data);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to fetch invites");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && user && user.role === 'admin') {
      fetchInvites();
    }
  }, [isAuthenticated, user]);

  const handleCreateInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);

    try {
      const response = await fetch("/api/admin/invites", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, role }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to create invite");
      }

      toast.success("Invite created successfully");
      setEmail("");
      setRole("operator");
      fetchInvites(); // Refresh list

      // Copy invite URL to clipboard
      if (data.inviteUrl) {
        await navigator.clipboard.writeText(data.inviteUrl);
        toast.success("Invite URL copied to clipboard");
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create invite");
    } finally {
      setCreating(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchInvites().finally(() => setRefreshing(false));
  };

  // Show loading while checking auth
  if (!isAuthenticated || !user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="text-muted-foreground">Loading...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show error if not admin
  if (user.role !== 'admin') {
    return null; // Will redirect
  }

  return (
    <div className="container mx-auto py-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">User Invites</h1>
          <p className="text-muted-foreground">Create and manage user invites</p>
        </div>
        <Button
          variant="outline"
          size="icon"
          onClick={handleRefresh}
          disabled={refreshing}
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Create invite form */}
      <Card>
        <CardHeader>
          <CardTitle>Create New Invite</CardTitle>
          <CardDescription>
            Send an invite link to let users create their own passwords
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleCreateInvite} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="user@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={creating}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Select
                value={role}
                onValueChange={(value: "operator" | "admin" | "supervisor") => setRole(value)}
                disabled={creating}
              >
                <SelectTrigger id="role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="operator">Operator</SelectItem>
                  <SelectItem value="supervisor">Supervisor</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={creating}
            >
              <Plus className="mr-2 h-4 w-4" />
              {creating ? "Creating..." : "Create Invite"}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Existing invites */}
      <Card>
        <CardHeader>
          <CardTitle>Existing Invites</CardTitle>
          <CardDescription>
            Track who has been invited and their status
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex flex-col items-center gap-4 py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="text-muted-foreground">Loading invites...</p>
            </div>
          ) : invites.length === 0 ? (
            <div className="flex flex-col items-center gap-4 py-8">
              <AlertCircle className="h-12 w-12 text-muted-foreground" />
              <p className="text-muted-foreground text-center">
                No invites created yet
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {invites.map((invite) => {
                const appBaseUrl = process.env.APP_BASE_URL || 'http://localhost:3000';
                const inviteUrl = `${appBaseUrl}/invite/${invite.id}`; // Using invite.id as token
                
                return (
                  <div
                    key={invite.id}
                    className="flex items-start justify-between rounded-lg border p-4 hover:bg-accent/50 transition-colors"
                  >
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{invite.email}</p>
                        <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary">
                          {invite.role}
                        </span>
                      </div>
                      <div className="flex gap-4 text-xs text-muted-foreground">
                        <span>Created: {new Date(invite.createdAt).toLocaleDateString()}</span>
                        {invite.usedAt && (
                          <span>Used: {new Date(invite.usedAt).toLocaleDateString()}</span>
                        )}
                        {!invite.usedAt && invite.expiresAt && (
                          <span>
                            Expires: {new Date(invite.expiresAt).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                      {invite.usedAt ? (
                        <span className="text-xs text-green-600">Accepted ✓</span>
                      ) : invite.expiresAt && new Date(invite.expiresAt) < new Date() ? (
                        <span className="text-xs text-destructive">Expired</span>
                      ) : (
                        <span className="text-xs text-primary">Pending</span>
                      )}
                    </div>
                    {!invite.usedAt && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          navigator.clipboard.writeText(inviteUrl);
                          toast.success("Invite URL copied to clipboard");
                        }}
                        title="Copy invite URL"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
